import type { LooseObject } from '@rentguru/commons-utils';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { FORBIDDEN_OBJECT_KEYS } from '../constants';

export const recordWasUpdated = (
  oldRecord: LooseObject,
  newRecord: LooseObject,
  additionalUpdateComparator?: (oldRecord: LooseObject, newRecord: LooseObject) => boolean
) => {
  // Custom comparator => Necessary for complex objects comparisons
  if (additionalUpdateComparator && additionalUpdateComparator(oldRecord, newRecord)) return true;
  for (const [key, value] of Object.entries(newRecord)) {
    // Classic comparator between record and its subscription
    if (key in oldRecord && !FORBIDDEN_OBJECT_KEYS.includes(key) && !isEqual(oldRecord[key], value)) {
      return true;
    }
  }
  return false;
};

const NOT_DELETED_FILTER: LooseObject = {
  or: [{ _deleted: { attributeExists: false } }, { _deleted: { eq: false } }],
};

export const excludeDeletedFromFilter = (filter: LooseObject | null): LooseObject => {
  if (isNil(filter)) {
    return NOT_DELETED_FILTER;
  }
  if (Array.isArray(filter.and)) {
    return { ...filter, and: [...filter.and, NOT_DELETED_FILTER] };
  }
  return { and: [filter, NOT_DELETED_FILTER] };
};

export const getFilters = (indexName?: string, indexValue?: string, additionalFilter?: LooseObject) => {
  if (!indexName && !indexValue) {
    return additionalFilter ?? null;
  }

  const filter = { and: [{ [indexName!]: { eq: indexValue } }] };
  if (additionalFilter) {
    filter.and.push(additionalFilter);
  }

  return filter;
};

export const getFilterFieldNameForIndex = (
  indexName:
    | 'byClientId'
    | 'byBuilding'
    | 'byContact'
    | 'byUnit'
    | 'byUnitId'
    | 'byOwner'
    | 'byParent'
    | 'byEmail'
    | 'byLeaseId'
    | 'byForeignKey'
    | 'byCategory'
    | 'byEntity'
) => {
  if (indexName === 'byBuilding') return 'buildingId';
  if (indexName === 'byContact') return 'contactId';
  if (indexName === 'byUnit' || indexName === 'byUnitId') return 'unitId';
  if (indexName === 'byOwner') return 'ownerId';
  if (indexName === 'byParent') return 'parentId';
  if (indexName === 'byEmail') return 'email';
  if (indexName === 'byLeaseId') return 'leaseId';
  if (indexName === 'byForeignKey') return 'foreignKey';
  if (indexName === 'byCategory') return 'categoryId';
  if (indexName === 'byEntity') return 'entity';

  return 'clientId';
};

export const getGraphQlMethodName = (query: string, type: 'mutation' | 'query') => {
  const operationName = new RegExp(`${type}\\s+(\\w+)`).exec(query)?.[1] ?? '';
  const method = operationName.charAt(0).toLowerCase() + operationName.slice(1);
  return method;
};

export const cleanMixedDatastoreAndAPISubscription = (subscriptionQuery: string) =>
  subscriptionQuery.replaceAll(/(updatedAt|createdAt)/gm, '');

export const cleanSyncQueries = (syncQuery: string) => syncQuery.replaceAll(/\b\w+\s*{[^{}]*}\s*/gs, '');

export const removeTechnicObject = (query: string) =>
  query.replaceAll(/\btechnic\b\s*{(?:[^{}]*{[^{}]*}[^{}]*)*}/g, '');

export const removeAddressObject = (query: string) => query.replaceAll(/address\s*{[^}]*}/g, '');

export const removeBuildingObject = (query: string) => query.replaceAll(/building\s*{[^}]*}/g, '');

// export const removeParametersFromQuery = (query: string, parameter: string) => {
//   const regex = new RegExp(`${parameter}\\s*{(?:[^{}]*|{(?:[^{}]*|{[^{}]*})*})*}`, 'g');
//   return query.replace(regex, '');
// };

export const removeParametersFromQuery = (query: string, parameter: string) =>
  query.replaceAll(new RegExp(String.raw`/${parameter}\s*{[^}]*\{[^}]*\}[^}]*}|${parameter}\s*{[^}]*}/g`, 'g'), '');
